import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { usePaymentAccounts } from '@/api/rpc/hooks/integrations/usePaymentAccounts';
import { useUpdatePayment } from '@/api/rpc/hooks/finance/useUpdatePayment';
import { Skeleton } from '@/components/ui/skeleton';
import { T } from 'gt-react';

interface PaymentAccountSelectorProps {
  paymentId: string;
  currentAccountId: string | null | undefined;
  disabled?: boolean;
}

const AUTO_VALUE = '__auto__';

export function PaymentAccountSelector({
  paymentId,
  currentAccountId,
  disabled,
}: PaymentAccountSelectorProps) {
  const { verifiedAccounts, isLoading } = usePaymentAccounts();
  const updatePayment = useUpdatePayment();

  if (isLoading) {
    return <Skeleton className="h-8 w-full" />;
  }

  if (verifiedAccounts.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        <T context="ap">No verified accounts</T>
      </p>
    );
  }

  function handleChange(value: string) {
    const accountId = value === AUTO_VALUE ? null : value;
    updatePayment.mutate({
      id: paymentId,
      data: { paymentAccountId: accountId },
    });
  }

  return (
    <Select
      value={currentAccountId ?? AUTO_VALUE}
      onValueChange={handleChange}
      disabled={disabled || updatePayment.isPending}
    >
      <SelectTrigger className="h-8 text-sm">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={AUTO_VALUE}>
          <T context="ap">Auto (Default)</T>
        </SelectItem>
        {verifiedAccounts.map((account) => (
          <SelectItem key={account.id} value={account.id}>
            <span className="flex items-center gap-2">
              {account.name} &middot; {account.institutionName} (****
              {account.accountMask})
              {account.isDefault && (
                <Badge variant="outline" className="text-[10px] px-1 py-0">
                  <T context="payment account default badge">Default</T>
                </Badge>
              )}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
